import React, { useEffect } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material/';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  search?: string;
  setGlobalFilter?: (filter: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minLength?: number;
}

const Search: React.FC<Props> = ({
  search = '',
  setGlobalFilter,
  placeholder,
  disabled,
  className,
  minLength = 2,
}) => {
  const [value, setValue] = React.useState(search);
  const didMountRef = React.useRef(false);
  const timeout = React.useRef<ReturnType<typeof setTimeout>>();

  const handleChange = React.useCallback(
    (val) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      if (!setGlobalFilter) return;
      timeout.current = setTimeout(() => {
        setGlobalFilter(val || '');
      }, 500);
    },
    [setGlobalFilter],
  );

  useEffect(() => setValue(search), [search]);

  useEffect(() => {
    if (didMountRef.current && (!value || value.length >= minLength)) {
      handleChange(value);
    } else {
      didMountRef.current = true;
    }
  }, [value, minLength, handleChange]);

  return (
    <TextField
      fullWidth
      className={className}
      data-testid="search-box"
      type="search"
      variant="outlined"
      disabled={disabled}
      value={value || ''}
      onChange={(e) => { setValue(e.target.value); }}
      sx={{
        backgroundColor: (theme) => theme.palette.common.white,
        '& input::-webkit-search-decoration, & input::-webkit-search-cancel-button, & input::-webkit-search-results-button, & input::-webkit-search-results-decoration': {
          display: 'none',
        },
        // MUI renders a notched outline with `top: -5px` to carve space for a
        // floating label. This component has no label, so the notch is hidden
        // and the offset corrected to -1px (the border width). Without this,
        // the top border clips slightly. Note: Storybook wraps stories in
        // CssBaseline which resets fieldset styles, masking the issue there.
        // In the real app, legacy Bootstrap/normalize CSS makes the legend
        // element visible, so `display: none` is also required to keep it
        // hidden and prevent it from creating an unwanted gap in the border.
        '& .MuiOutlinedInput-notchedOutline': {
          top: '-1px',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
          display: 'none',
        },
      }}
      placeholder={placeholder ?? 'Search'}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {value ? (
              <IconButton
                aria-label="clear search"
                onClick={() => setValue('')}
                edge="end"
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : (
              <SearchIcon />
            )}
          </InputAdornment>
        ),
      }}
      size="small"
      onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
    />
  );
};

export default React.memo(Search);
